/**
 * /catalog — unified browse-everything Olytic offers. Three tabs (Models,
 * Connectors, Prompts — D-167 order) per Push #3.2.5 (OC adjudication
 * 2026-06-09).
 *
 * Mental model split:
 *   - Per-pillar pages (/connectors, /knowledge, /models) = your stuff.
 *   - This Catalog = the marketplace.
 *
 * Each tab renders platform-tier templates with status-aware actions:
 *   - status='active'      → Install button (admin) / Request access (user)
 *   - status='coming_soon' → greyed-out card + "Coming soon" badge + Suggest
 *                            priority (any role)
 *
 * Sort: active first, coming_soon second. Archived rows are filtered server-
 * side so they never appear here.
 *
 * Data: /api/catalog/all (one round-trip, returns all 3 pillars).
 * Install actions still route through admin-only POST endpoints so the gate
 * remains where it should — read here, write through admin paths.
 *
 * Push #4 (2026-06-25): extracted sub-modules into `./library/` along the
 * natural seams (CatalogTile primitive + per-pillar shims + section shell +
 * skeleton). This file is now an orchestrator — it owns state, fetch, and
 * the LightningTabs glue; rendering work lives in `./library/`.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@olytic/foundation';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import {
  fetchUnifiedCatalog,
  installConnector,
  cloneModelTemplate,
  ApiError,
  castVote,
  type UnifiedConnectorTpl,
  type UnifiedModelTpl,
  type UnifiedKnowledgeTpl,
  type RequestType,
} from '../lib/api.ts';

// Narrow literal matching castVote's request_type argument (D-156 catalog
// vote signal). Distinct from the wider `RequestType` export above — that
// type is the sys__request.request_type union ('connector_access',
// 'suggestion', etc.), used by the RequestModal flow. CatalogVoteType is
// just the three pillar slugs castVote accepts.
type CatalogVoteType = 'connector' | 'knowledge' | 'model';
import { ObjectIcon } from '../components/ObjectIcon.tsx';
import { useToast, showApiErrorToast } from '../components/Toast.tsx';
import { RequestModal } from '../components/RequestModal.tsx';
import { LightningTabs, type LightningTab } from '../components/LightningTabs.tsx';

import { CatalogTabBody } from './library/CatalogTabBody.tsx';
import { ConnectorTile, ModelTile } from './library/PillarTiles.tsx';
import { LibrarySkeleton } from './library/LibrarySkeleton.tsx';
import { RouterNavLink } from '../lib/foundationLinks.tsx';

interface CatalogState {
  connectors: UnifiedConnectorTpl[];
  models: UnifiedModelTpl[];
  knowledge: UnifiedKnowledgeTpl[];
}

export function Market() {
  const { user } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  // Push #31: Catalog routes mount under both /catalog (user mode) and
  // /admin/catalog (admin mode). Drill-down stays in the same mode the
  // admin came from — so an admin viewing the catalog from setup lands on
  // /admin/catalog/<slug>, not the user-mode /catalog/<slug>.
  const catalogBase = location.pathname.startsWith('/admin/') ? '/admin/market' : '/market';
  const { showToast } = useToast();
  const [data, setData] = useState<CatalogState | null>(null);
  // Migration H (2026-06-24): The dedicated SuggestNewModal retired with the
  // sys__request table. The "Suggest something new" surface is now the
  // shared RequestModal (Push #92) pre-routed to Olytic with the
  // 'suggestion' kind. Triggered by the header CTA on this page.
  // Per Ean 2026-06-24: Upvote on Coming Soon cards is one-click. We track
  // which targets the user upvoted this session locally so the chip flips
  // to filled-heart + the count optimistically increments. The server is
  // authoritative on idempotence via sys__account_user.ui_preferences
  // .upvoted_slugs; the /api/me/vote response carries both the new count
  // and an already_upvoted flag we use to reconcile.
  const [upvotedKeys, setUpvotedKeys] = useState<Set<string>>(new Set());

  async function handleUpvote(requestType: CatalogVoteType, targetSlug: string, _displayName: string) {
    const key = `${requestType}:${targetSlug}`;
    if (upvotedKeys.has(key)) return; // already upvoted this session; ignore
    // Optimistic: flip local state immediately so the chip changes.
    setUpvotedKeys((prev) => new Set(prev).add(key));
    try {
      await castVote(requestType, targetSlug);
      showToast({ kind: 'success', message: 'Thanks. Your upvote helps Olytic prioritize what ships next.' });
    } catch (err) {
      // Roll back optimistic flip.
      setUpvotedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      showToast({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not upvote.' });
    }
  }
  const [error, setError] = useState<string | null>(null);
  // Deep-link tab selection per Ean 2026-06-09: /catalog?tab=prompts or
  // ?tab=models lands on that pillar's tab. The pillar list pages
  // (Models / Connectors / Prompts — D-167 order) link here with their
  // matching tab so "Browse catalog" never dumps you on the wrong pillar.
  //
  // Backward compat: ?tab=knowledge (old name) still maps to the prompts tab
  // so existing bookmarks/redirects keep working.
  // D-167: default tab is models (first pillar in M → C → P order).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // Push 4-1b (2026-07-01): Market surgery removed the Prompts pillar.
  // Legacy ?tab=prompts / ?tab=knowledge deep-links fall through to the
  // default (models). Only 'models' + 'connectors' remain here.
  const initialTab: 'models' | 'connectors' =
    tabParam === 'connectors' ? 'connectors' : 'models';
  const [activeTab, setActiveTab] = useState<'models' | 'connectors'>(initialTab);
  const [installingId, setInstallingId] = useState<string | null>(null);
  // M1.5 Wave C / C1 — optimistic install pillkeys.
  //
  // The Install button used to wait for the network round-trip before flipping
  // the pill to "Installed ✓" (typical Cloud Run cold start can push that past
  // a second). Per the acceptance lock, the pill must flip within 100 ms of
  // click; the network finalizes in the background; on error, the pill reverts
  // and a toast surfaces.
  //
  // Implementation: a set of optimistic keys, one per tile (`connector:<name>`,
  // `model:<model_id>`, `knowledge:<name>`). The renderer ORs this against the
  // server's `is_installed_in_workspace` flag when deciding which pill to show.
  // On success we keep the key in the set + the next /api/catalog/all refetch
  // (next visit / refresh) will return is_installed_in_workspace=true so the
  // optimistic key is redundant but harmless. On error we drop the key + surface
  // the error toast via showApiErrorToast so admins see the friendly + raw
  // detail. The card never reloads — TileGrid re-renders from React state.
  const [optimisticInstalled, setOptimisticInstalled] = useState<Set<string>>(new Set());

  function addOptimistic(key: string) {
    setOptimisticInstalled((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }
  function removeOptimistic(key: string) {
    setOptimisticInstalled((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }
  const [requestModal, setRequestModal] = useState<{
    open: boolean;
    destination: 'admin' | 'olytic';
    initialKind: RequestType;
    templateName?: string;
    subjectHint?: string;
  }>({ open: false, destination: 'olytic', initialKind: 'new_connector' });

  useEffect(() => {
    let cancelled = false;
    fetchUnifiedCatalog()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load catalog.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (user === null) return null;
  const canInstall = user.role === 'owner' || user.role === 'admin';

  // Counts — used for the highlight scan line + tab badges.
  const connectorActive = useMemo(
    () => (data?.connectors ?? []).filter((t) => t.status === 'active').length,
    [data],
  );
  const connectorComingSoon = useMemo(
    () => (data?.connectors ?? []).filter((t) => t.status === 'coming_soon').length,
    [data],
  );

  // M1.5 Wave C / C1 — optimistic install handlers.
  //
  // Shape:
  //   1. Flip optimistic pill immediately (addOptimistic).
  //   2. Set installingId so the underlying button hides while the bottom-row
  //      action collapses (Installed tile has no bottom action — the whole
  //      tile becomes the click target). installingId stays set briefly until
  //      the network finalizes so a flailing user can't double-fire.
  //   3. Fire the network call in the background.
  //   4. Success: keep optimistic flip + show subtle confirmation toast.
  //   5. Error: revert optimistic flip + showApiErrorToast (friendly + raw
  //      detail + auto-dismiss-12s on errors with detail).
  //
  // Per Wave A's disconnect pattern in ConnectorDetail: we deliberately do NOT
  // re-fetch /api/catalog/all after success — the optimistic state already
  // matches what the server will return on the next natural reload, and a
  // forced refetch would steal the catalog's scroll/search/filter state.
  async function handleInstallConnector(tpl: UnifiedConnectorTpl) {
    const key = `connector:${tpl.name}`;
    addOptimistic(key);
    setInstallingId(tpl.id);
    try {
      await installConnector(tpl.name);
      showToast({
        kind: 'success',
        message: `${tpl.display_name} installed. Find it under Connectors.`,
      });
    } catch (err) {
      // Roll back optimistic flip so the pill returns to its pre-click state
      // (no orphan "installed" tiles for things that aren't actually installed).
      removeOptimistic(key);
      showApiErrorToast(
        showToast,
        err,
        `Couldn't install ${tpl.display_name}. Try again in a moment.`,
      );
    } finally {
      setInstallingId(null);
    }
  }
  // Push 4-1b (2026-07-01): handleInstallKnowledge removed — Prompts are
  // Group 2 BYO and no longer surface on Market (see tab config below).
  async function handleInstallModel(tpl: UnifiedModelTpl) {
    const key = `model:${tpl.model_id}`;
    addOptimistic(key);
    setInstallingId(tpl.id);
    try {
      await cloneModelTemplate(tpl.id);
      showToast({
        kind: 'success',
        message: `${tpl.display_name} installed. Find it under Models.`,
      });
    } catch (err) {
      removeOptimistic(key);
      showApiErrorToast(
        showToast,
        err,
        `Couldn't install ${tpl.display_name}. Try again in a moment.`,
      );
    } finally {
      setInstallingId(null);
    }
  }

  function openRequestAccess(name: string, displayName: string, kind: RequestType) {
    setRequestModal({
      open: true,
      destination: 'admin',
      initialKind: kind,
      templateName: name,
      subjectHint: `Request access to ${displayName}`,
    });
  }
  function openSuggestPriority(name: string, displayName: string, kind: RequestType) {
    setRequestModal({
      open: true,
      destination: 'olytic',
      initialKind: kind,
      templateName: name,
      subjectHint: `Prioritize ${displayName}`,
    });
  }
  function openHeaderRequest() {
    setRequestModal({
      open: true,
      destination: 'olytic',
      initialKind: 'new_connector',
    });
  }

  return (
    <div>
      {/* Push #28. Catalog highlight reverted to focused shape. Mini-stat
          tiles from Push #22 overlapped the tab strip below; tabs already
          carry per-pillar nav with counts. Highlight is now ObjectIcon +
          title + 1-line description + secondary "Request something new"
          button top-right. Per-pillar icons live on the tabs instead. */}
      <div className="lightning-highlight">
        <div className="lightning-highlight-left">
          <ObjectIcon kind="catalog" size="lg" />
          <div className="lightning-highlight-text">
            <h1 className="lightning-title">Market</h1>
            <p
              className="muted"
              style={{ margin: '6px 0 0', fontSize: '13px', maxWidth: '72ch', lineHeight: 1.45 }}
            >
              Everything Olytic offers: browse, install, request.
            </p>
          </div>
        </div>
        <div className="lightning-highlight-actions">
          <Button type="button" variant="outline" onClick={openHeaderRequest}>
            Request something new
          </Button>
        </div>
      </div>

      {error !== null && (
        <div className="callout callout-danger" style={{ marginBottom: '16px' }}>
          <p className="callout-title">Couldn't load the market</p>
          <p className="callout-body">{error}</p>
        </div>
      )}

      {data === null && error === null && (
        <LibrarySkeleton />
      )}

      {data !== null && (
        // Full-width per Ean 2026-06-09 — Catalog has no side rail content,
        // so the record-shell 2/3-with-rail layout was wasting the right
        // third. Catalog tabs span the full column the way the highlight
        // panel does.
        <div className="record-surface record-surface--tabs-only">
          {(() => {
            // Push 4-1b (2026-07-01): Market surgery — Prompts pillar
            // card removed. Market is Group 1 (sourced/installable) only
            // per LOCKED MENTAL MODEL. Prompts are Group 2 BYO — authored
            // in Capabilities, not installed via Market. Only Connectors +
            // Models remain here.
            const tabs: LightningTab[] = [
              {
                id: 'models',
                label: 'Models',
                icon: <ObjectIcon kind="models" size="xs" />,
                count: data.models.length,
                render: () => (
                  <CatalogTabBody
                    available={data.models.filter((t) => t.status === 'active')}
                    comingSoon={data.models.filter((t) => t.status === 'coming_soon')}
                    pillarLabel="models"
                    renderTile={(t, opts) => (
                      <ModelTile
                        key={t.id}
                        tpl={t}
                        installing={installingId === t.id}
                        canInstall={canInstall}
                        onInstall={() => void handleInstallModel(t)}
                        onRequestAccess={() => openRequestAccess(t.model_id, t.display_name, 'suggestion')}
                        onSuggestPriority={() => openSuggestPriority(t.model_id, t.display_name, 'suggestion')}
                        upvoteCount={t.upvote_count}
                        // C1: optimistic OR server-truth. Server flips on the
                        // next /api/catalog/all reload; optimistic carries the
                        // flip in the meantime.
                        isInstalled={t.is_installed_in_workspace || optimisticInstalled.has(`model:${t.model_id}`)}
                        onUpvote={() => void handleUpvote('model', t.model_id, t.display_name)}
                        hasUpvoted={upvotedKeys.has(`model:${t.model_id}`)}
                        {...(opts.comingSoon
                          ? {}
                          : {
                              // Push 4-1d-m (2026-07-06): model tile drills to
                              // the template detail (workspace_directory.
                              // llm__model_template) at /market/models/:short_id,
                              // not to the instance. Same discipline as connector
                              // tile: market always routes to templates. Template
                              // short_id (mdt-prefix) is always present, so the
                              // prior instance_short_id === null gate is gone.
                              onOpen: () => navigate(`${catalogBase}/models/${t.short_id}`),
                            })}
                      />
                    )}
                    matchesQuery={(t, q) => {
                      const hay = `${t.display_name} ${t.description ?? ''} ${t.provider_display_name}`.toLowerCase();
                      return hay.includes(q);
                    }}
                  />
                ),
              },
              {
                id: 'connectors',
                label: 'Connectors',
                icon: <ObjectIcon kind="connectors" size="xs" />,
                count: connectorActive + connectorComingSoon,
                render: () => (
                  <CatalogTabBody
                    available={data.connectors.filter((t) => t.status === 'active')}
                    comingSoon={data.connectors.filter((t) => t.status === 'coming_soon')}
                    pillarLabel="connectors"
                    renderTile={(t, opts) => (
                      <ConnectorTile
                        key={t.id}
                        tpl={t}
                        installing={installingId === t.id}
                        canInstall={canInstall}
                        onInstall={() => void handleInstallConnector(t)}
                        onRequestAccess={() => openRequestAccess(t.name, t.display_name, 'connector_access')}
                        onSuggestPriority={() => openSuggestPriority(t.name, t.display_name, 'new_connector')}
                        upvoteCount={t.upvote_count}
                        // C1: optimistic OR server-truth.
                        isInstalled={t.is_installed_in_workspace || optimisticInstalled.has(`connector:${t.name}`)}
                        onUpvote={() => void handleUpvote('connector', t.name, t.display_name)}
                        hasUpvoted={upvotedKeys.has(`connector:${t.name}`)}
                        {...(opts.comingSoon
                          ? {}
                          : { onOpen: () => navigate(`${catalogBase}/connectors/${t.short_id}`) })}
                      />
                    )}
                    matchesQuery={(t, q) => {
                      const hay = `${t.display_name} ${t.description ?? ''}`.toLowerCase();
                      return hay.includes(q);
                    }}
                  />
                ),
              },
              // Push 4-1b (2026-07-01): Prompts tab REMOVED from Market.
              // Prompts are Group 2 BYO (authored, not sourced) per
              // LOCKED MENTAL MODEL. They live under Capabilities → Prompts,
              // not on the Market discovery surface. Prompts template data
              // still returns from /api/catalog/all (leave server as-is);
              // this tab config is the display gate.
            ];
            return (
              <LightningTabs
                tabs={tabs}
                value={activeTab}
                onChange={(id) => {
                  const next = id as 'models' | 'connectors';
                  setActiveTab(next);
                  // Mirror the active tab into the URL so refresh + back-button
                  // land on the same tab the user was viewing (D-153 drill-down
                  // discipline: state visible in the URL).
                  setSearchParams({ tab: next }, { replace: true });
                }}
              />
            );
          })()}
        </div>
      )}

      <RequestModal
        open={requestModal.open}
        onClose={() => setRequestModal((s) => ({ ...s, open: false }))}
        destination={requestModal.destination}
        initialKind={requestModal.initialKind}
        {...(requestModal.templateName !== undefined ? { initialTemplateName: requestModal.templateName } : {})}
        {...(requestModal.subjectHint !== undefined ? { subjectHint: requestModal.subjectHint } : {})}
      />

      {/* Catalog whisper. reassures non-admins that requesting isn't
         risky: admins approve before anything turns on, per Ean 2026-06-09
         "reinforce value subtly everywhere". */}
      <div className="trust-whisper trust-whisper--footer">
        Nothing turns on until your admin approves it · Every request is logged
      </div>
      {/* Migration H (2026-06-24): suggest-new now uses the shared RequestModal
         (Push #92) routed to Olytic with the 'suggestion' kind. The standalone
         SuggestNewModal retired with the sys__request table. */}
      <div style={{ marginTop: 12, textAlign: 'center', fontSize: 13 }}>
        <Button
          type="button"
          variant="link"
          onClick={() =>
            setRequestModal({
              open: true,
              destination: 'olytic',
              initialKind: 'suggestion',
            })
          }
        >
          Don't see what you need? Suggest it →
        </Button>
      </div>
      {/* Push 84b (D-199) — BYO Pattern C discovery link. Admins with their
         own MCP can bring it via /admin/connectors/byo; the marketplace itself
         remains Olytic-vouched only (Pattern A + B). This link is a discovery
         affordance, NOT a marketplace tab — Pattern C never surfaces in the
         marketplace grid to preserve the trust-surface separation. Muted
         styling keeps it below the fold. Available on both /market (user
         mode) and /admin/market (admin mode) so admins hit it either way. */}
      {canInstall && (
        <div style={{ marginTop: 6, textAlign: 'center', fontSize: 12 }}>
          <Button asChild variant="link">
            <RouterNavLink href="/admin/connectors/byo" style={{ color: 'var(--fg-muted)' }}>
              Have your own MCP? Bring it →
            </RouterNavLink>
          </Button>
        </div>
      )}
    </div>
  );
}
